import { Injectable, isDevMode } from '@angular/core';

/**
 * Canary icon — bắt đúng thứ mà KHÔNG công cụ tĩnh nào bắt được: tên icon là DỮ LIỆU.
 *
 * Ba tầng phòng thủ của hệ này, và trần của từng tầng:
 *   1. `icons:check` (regex)  — chỉ thấy tên viết thẳng trong src/.
 *   2. `MaterialIconName`     — trình biên dịch phủ mọi luồng CÓ KIỂU. Trần của nó là ranh
 *                               giới dữ liệu: tên đến từ HTTP/DB thì tsc không biết gì.
 *   3. tệp này                — nhìn DOM thật sau khi render, nên thấy cả hai loại trên.
 *
 * Vì sao cần thật (đo 2026-08-09): `xnk-hub.component.html` render `{{ item.icon }}` bằng font,
 * giá trị đến thẳng từ `GetXnkHubSummaryHandler.cs`. `KnowledgeCategory.Icon`, `KPIFormula.Icon`,
 * `Allergen.IconCode` còn nằm trong DATABASE, do người dùng gõ. Glyph thiếu KHÔNG sinh lỗi nào —
 * trình duyệt lặng lẽ in nguyên tên ligature ra giữa giao diện.
 *
 * Chỉ chạy ở dev. Danh sách tên (~9 KB) nạp bằng `await import()` SAU khi đã kiểm `isDevMode()`,
 * nên production không bao giờ tải chunk đó.
 */
@Injectable({ providedIn: 'root' })
export class IconCanaryService {
  private names: ReadonlySet<string> | null = null;
  private observer: MutationObserver | null = null;
  /** Báo mỗi tên đúng MỘT lần — một icon hỏng trong bảng 50 dòng sẽ nổ 50 lần nếu không chặn. */
  private readonly reported = new Set<string>();
  private queued = false;

  async start(): Promise<void> {
    if (!isDevMode() || typeof document === 'undefined') return;

    const { MATERIAL_ICON_NAMES } = await import('../constants/material-icon-name.catalog');
    this.names = MATERIAL_ICON_NAMES;

    this.scan(document.body);
    this.observer = new MutationObserver(() => this.schedule());
    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  stop(): void {
    this.observer?.disconnect();
    this.observer = null;
  }

  /**
   * Gom nhiều mutation vào một lần quét. Router chuyển trang có thể bắn hàng nghìn mutation;
   * quét ngay trong callback sẽ biến canary thành thứ làm chậm dev.
   */
  private schedule(): void {
    if (this.queued) return;
    this.queued = true;
    requestAnimationFrame(() => {
      this.queued = false;
      this.scan(document.body);
    });
  }

  private scan(root: ParentNode): void {
    if (!this.names) return;
    for (const el of Array.from(root.querySelectorAll('.material-symbols-outlined'))) {
      const text = (el.textContent ?? '').trim();
      // Rỗng = chưa đặt tên (ds-icon ở nhánh svg). Có khoảng trắng = không phải ligature.
      if (!text || /\s/.test(text)) continue;
      if (this.names.has(text)) continue;
      if (this.reported.has(text)) continue;
      this.reported.add(text);
      console.error(
        `[icon-canary] "${text}" không có glyph trong font subset — nó đang hiện ra CHỮ THÔ.\n` +
          `  Nếu là tên icon thật: thêm vào mã rồi chạy \`npm run icons:build\`.\n` +
          `  Nếu tên đến từ backend/DB: sửa dữ liệu, hoặc thêm vào EXTERNAL_FONT_ICONS.\n` +
          `  Nếu là tên sprite (có tiền tố namespace): dùng [svgIcon] chứ không phải font.`,
        el,
      );
    }
  }
}
